import React from 'react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { QueryClient } from '@tanstack/react-query';
import type { EventObject } from '../eventTypes';

/**
 * Paired-event DELETE engine for EventListModule (#3277).
 *
 * `handleDeleteEvent` deletes the primary event, then — for hotel and flight
 * events — locates and deletes the PAIRED counterpart (check-in/check-out, or
 * the other flight leg) before invalidating the events query. The
 * primary-then-pair-then-invalidate ordering and the try/catch that swallows
 * failures with a `console.error` are load-bearing; behavior is pinned by
 * `useEventDeletion.char.test.ts`.
 */
export interface UseEventDeletionOptions {
  allEvents: any[];
  token: string | null;
  queryClient: QueryClient;
}

export function useEventDeletion(opts: UseEventDeletionOptions) {
  const { allEvents, token, queryClient } = opts;

  async function handleDeleteEvent(eventId: string, e?: React.MouseEvent) {
    if (e) {
      e.stopPropagation();
    }

    try {
      const eventToDelete = allEvents.find((ev: EventObject) => ev.id === eventId);

      await api.objects.deleteObject(token ?? null, 'event', eventId);

      if (eventToDelete?.eventType === 'hotel' && eventToDelete?.hotelPairId) {
        const pairedEvent = allEvents.find(
          (ev: EventObject) => ev.hotelPairId === eventToDelete.hotelPairId && ev.id !== eventId
        );
        if (pairedEvent) {
          console.log('[EventListModule] Deleting paired hotel event:', pairedEvent.id);
          await api.objects.deleteObject(token ?? null, 'event', pairedEvent.id);
        }
      }

      if (eventToDelete?.flightPairId && eventToDelete?.flightLeg) {
        const pairedEvent = allEvents.find(
          (ev: EventObject) =>
            ev.flightPairId === eventToDelete.flightPairId &&
            ev.id !== eventId
        );
        if (pairedEvent) {
          console.log('[EventListModule] Deleting paired flight event:', pairedEvent.id);
          await api.objects.deleteObject(token ?? null, 'event', pairedEvent.id);
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
    } catch (err) {
      console.error('[EventListModule] Failed to delete event:', err);
    }
  }

  return { handleDeleteEvent };
}
